import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { RealtimePostgresChangesPayload } from "@supabase/supabase-js";

import { supabase } from "@/integrations/supabase/client";
import { useTenantStore } from "@/stores/tenantStore";

type RestaurantRow = {
  id: string;
  name: string | null;
  subdomain?: string | null;
  timezone?: string | null;
  currency?: string | null;
  tax_rate_pct?: number | null;
  service_charge_pct?: number | null;
};

const isRestaurantRow = (value: unknown): value is RestaurantRow => {
  if (!value || typeof value !== "object") {
    return false;
  }
  return typeof (value as RestaurantRow).id === "string";
};

export const useTenantProfileRealtime = (): void => {
  const queryClient = useQueryClient();
  const activeRestaurantId = useTenantStore((state) => state.activeRestaurantId);

  useEffect(() => {
    if (!activeRestaurantId) {
      return;
    }

    const storage = typeof window !== 'undefined' ? window.localStorage : null;
    const invalidateQuery = (
      filters: Parameters<typeof queryClient.invalidateQueries>[0],
    ): void => {
      queryClient.invalidateQueries(filters).catch((error) => {
        if (import.meta.env?.DEV) {
          console.warn('[tenant-realtime] failed to invalidate query', filters, error);
        }
      });
    };

    const handleRestaurantChange = (payload: RealtimePostgresChangesPayload<RestaurantRow>): void => {
      const payloadRecord = payload.new ?? payload.old;
      if (!isRestaurantRow(payloadRecord)) {
        return;
      }
      const record = payloadRecord;

      const {
        activeSubdomain,
        slug,
        activeRestaurantName,
        isDemo,
        setActiveTenant,
      } = useTenantStore.getState();

      const resolvedSubdomain = activeSubdomain ?? record.subdomain ?? slug ?? null;
      if (!resolvedSubdomain) {
        return;
      }

      const nextName = record.name ?? activeRestaurantName ?? resolvedSubdomain;
      if (
        activeRestaurantName !== nextName ||
        activeSubdomain !== resolvedSubdomain
      ) {
        setActiveTenant(record.id, resolvedSubdomain, nextName, isDemo);
        if (storage) {
          try {
            storage.setItem(
              "restaurant_data",
              JSON.stringify({
                restaurant_id: record.id,
                subdomain: resolvedSubdomain,
                name: nextName,
              }),
            );
          } catch (storageError) {
            console.warn("[tenant-realtime] failed to persist restaurant_data", storageError);
          }
        }
      }

      invalidateQuery({ queryKey: ["settings", "restaurant"] });
      invalidateQuery({ queryKey: ["layout", "restaurant", record.id] });
    };

    const channel = supabase
      .channel(`tenant-profile-${activeRestaurantId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "restaurants",
          filter: `id=eq.${activeRestaurantId}`,
        },
        handleRestaurantChange,
      );

    const subscribeResult = channel.subscribe();
    if (subscribeResult instanceof Promise) {
      subscribeResult.catch((error) => {
        if (import.meta.env?.DEV) {
          console.warn('[tenant-realtime] failed to subscribe to channel', error);
        }
      });
    }

    return () => {
      supabase.removeChannel(channel).catch((error) => {
        if (import.meta.env?.DEV) {
          console.warn('[tenant-realtime] failed to remove channel', error);
        }
      });
    };
  }, [activeRestaurantId, queryClient]);
};
